/**
 * Hook para gerenciar estado e validação do formulário
 */

import { useState, useCallback, useEffect } from 'react';
import { Cliente, ProdutoCategoriaItem } from '../../../../types/gerArquivos';
import { GerArquivosService } from '../../../../services/gerArquivosService';
import { storage } from '../../../../lib/storage/storage';
import { NovoArquivoFormState, NovoArquivoSubmitData } from '../types';
import { isValidWhatsApp } from '../../../../utils/whatsapp';

export function useNovoArquivoForm() {
  const [form, setForm] = useState<NovoArquivoFormState>({
    cliente: null,
    prodCatItem: 1,
    observacoes: '',
    nomeOpcional: ''
  });

  const [categorias, setCategorias] = useState<ProdutoCategoriaItem[]>([]);
  const [carregandoCategorias, setCarregandoCategorias] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /**
   * Carrega categorias da API e restaura última seleção
   */
  useEffect(() => {
    (async () => {
      try {
        setCarregandoCategorias(true);
        const cats = await GerArquivosService.listarProdutoCategorias();
        setCategorias(cats);
        
        // Restaurar última categoria usada
        const saved = storage.get<string>('gerarq:last_cat', { area: 'local' });
        if (saved && cats?.some(c => String(c.id) === String(saved))) {
          setForm(prev => ({ ...prev, prodCatItem: Number(saved) as 1|2|3|4 }));
        } else if (cats && cats.length > 0) {
          setForm(prev => ({ ...prev, prodCatItem: cats[0].id as 1|2|3|4 }));
        }
      } catch (e) {
        setErro('Erro ao carregar categorias');
      } finally {
        setCarregandoCategorias(false);
      }
    })();
  }, []);

  /**
   * Atualiza o cliente no formulário
   */
  const setCliente = useCallback((cliente: Cliente | null) => {
    setForm(prev => ({ ...prev, cliente }));
  }, []);

  /**
   * Atualiza a categoria do produto
   */
  const setProdCatItem = useCallback((catId: 1 | 2 | 3 | 4) => {
    setForm(prev => ({ ...prev, prodCatItem: catId }));
    // Salvar preferência
    try {
      storage.set('gerarq:last_cat', String(catId), { area: 'local' });
    } catch { /* ignore */ }
  }, []);

  /**
   * Atualiza as observações
   */
  const setObservacoes = useCallback((obs: string) => {
    setForm(prev => ({ ...prev, observacoes: obs.slice(0, 500) }));
  }, []);

  /**
   * Atualiza o nome opcional (para sem cadastro)
   */
  const setNomeOpcional = useCallback((nome: string) => {
    setForm(prev => ({ ...prev, nomeOpcional: nome }));
  }, []);

  /**
   * Atualiza WhatsApp (para sem cadastro)
   */
  const setCliWhatsapp = useCallback((whatsapp: string) => {
    setForm(prev => ({ ...prev, cliWhatsapp: whatsapp }));
  }, []);

  /**
   * Valida o formulário antes de submeter
   */
  const validate = useCallback((digits: string): { valid: boolean; error?: string } => {
    if (!form.prodCatItem) {
      return { valid: false, error: 'Selecione uma categoria' };
    }

    // Se não tem cliente cadastrado, WhatsApp é obrigatório e deve ser válido
    if (!form.cliente) {
      if (!digits || digits.length === 0) {
        return { valid: false, error: 'Informe um cliente ou WhatsApp válido' };
      }
      
      if (!isValidWhatsApp(digits)) {
        return { 
          valid: false, 
          error: 'WhatsApp inválido. Deve ter 10 ou 11 dígitos válidos (DDD + número). Exemplo: 6291450653 ou 629991450653' 
        };
      }
    }

    return { valid: true };
  }, [form.cliente, form.prodCatItem]);

  /**
   * Prepara os dados para submit
   */
  const prepareSubmitData = useCallback((digits: string): NovoArquivoSubmitData => {
    return {
      cliente: form.cliente,
      prod_cat_item: form.prodCatItem,
      observacoes: form.observacoes,
      cli_whatsapp: form.cliente ? undefined : digits
    };
  }, [form]);

  /**
   * Reset do formulário
   */
  const reset = useCallback(() => {
    setForm({
      cliente: null,
      prodCatItem: 1,
      observacoes: '',
      nomeOpcional: ''
    });
    setErro(null);
  }, []);

  return {
    // Estado
    form,
    categorias,
    carregandoCategorias,
    erro,
    
    // Ações
    setCliente,
    setProdCatItem,
    setObservacoes,
    setNomeOpcional,
    setCliWhatsapp,
    setErro,
    validate,
    prepareSubmitData,
    reset
  };
}

